import { useCallback, useRef } from 'react'
import { useSelector } from 'react-redux'
import { editorSelectors } from 'store/features/editor/selectors'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { TIMELINE_DROP_ZONE_ID } from 'consts'
import {
  pixelsToSeconds,
  secondsToPixels,
  getInsertStartTimeForZIndex
} from 'utils/timeline'
import {
  resolveDropTargetFromCursor,
  resolveTargetZIndex
} from 'utils/timelineZIndex'

export interface IResolvedDropPosition {
  insertTime?: number
  previousItemId?: string
  targetZIndex?: number
  // Set when the cursor is over a `BetweenLinesGap` (or above the topmost
  // gap). UI consumers use this to highlight the specific gap. -1 means the
  // top gap (above the first row).
  targetGapAfterLineIndex?: number
}

// Shared coordinate-translation logic used by both the dnd-kit sidebar→timeline
// flow (EditorDndContext) and the native HTML5 OS-files flow
// (useTimelineDropZone). Keeps `existingMediaItems` behind a ref so the returned
// callbacks can stay stable — recreating them on every mediaItems change made
// dnd-kit loop on measurement inside the nested MultiLineDnd DndContext.
export const useTimelineDropPosition = () => {
  const timelineRulerSliceDuration = useSelector(
    editorSelectors.timelineRulerSliceDuration
  )
  const existingMediaItems = useSelector(projectDataSelectors.mediaItems)

  const existingMediaItemsRef = useRef(existingMediaItems)
  existingMediaItemsRef.current = existingMediaItems

  const getDropPositionFromClient = useCallback(
    (clientX: number, clientY: number): IResolvedDropPosition => {
      const contentWrapper = document.getElementById(TIMELINE_DROP_ZONE_ID)
      if (!contentWrapper) return {}

      const wrapperRect = contentWrapper.getBoundingClientRect()
      const dropX = clientX - wrapperRect.left
      const rawDropStartTime = Math.floor(
        Math.max(0, pixelsToSeconds(dropX, timelineRulerSliceDuration))
      )

      const target = resolveDropTargetFromCursor(clientY)
      const targetZIndex = resolveTargetZIndex(
        target,
        existingMediaItemsRef.current
      )
      const targetGapAfterLineIndex =
        target?.kind === 'gap' ? target.afterLineIndex : undefined

      const { insertTime, previousItemId } = getInsertStartTimeForZIndex(
        existingMediaItemsRef.current,
        rawDropStartTime,
        targetZIndex
      )
      return {
        insertTime,
        previousItemId,
        targetZIndex,
        targetGapAfterLineIndex
      }
    },
    [timelineRulerSliceDuration]
  )

  const getDropPositionFromDndKit = useCallback(
    (
      cursor: { x: number; y: number },
      ghostDuration: number
    ): IResolvedDropPosition => {
      const ghostWidth = secondsToPixels(
        ghostDuration,
        timelineRulerSliceDuration
      )
      return getDropPositionFromClient(cursor.x - ghostWidth / 2, cursor.y)
    },
    [timelineRulerSliceDuration, getDropPositionFromClient]
  )

  return { getDropPositionFromClient, getDropPositionFromDndKit }
}
